import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import Breadcrumbs from '../components/Breadcrumbs';
import '../App.css';
import '../components/SearchResults.css';
import './DetailPage.css';

interface Livre {
    id: number;
    title?: string;
    titlefr?: string;
    titlear?: string;
    auteur?: string;
    editeur?: string;
    isbn?: string;
    ibsn?: string; // Keep both for safety if backend uses typo
    book?: string;
    pdf_url?: string;
    pdf_ar_url?: string;
    nbre_pages?: number;
    annee?: number;
    contenu?: string;
    resume?: string;
}

const LivresDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [livre, setLivre] = useState<Livre | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchLivre();
    }, [id, i18n.language]);

    const fetchLivre = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/livre/${id}?lang=${i18n.language}`);
            setLivre(response.data);
        } catch (err) {
            console.error('Error fetching livre:', err);
            setError('Erreur lors du chargement du livre');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container mt-5">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !livre) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">{error || 'Livre non trouvé'}</div>
                <button className="btn btn-primary" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left me-2"></i>Retour
                </button>
            </div>
        );
    }

    const displayTitle = i18n.language === 'ar'
        ? (livre.titlear || livre.titlefr || livre.title)
        : (livre.titlefr || livre.title || livre.titlear);

    const ebookLink = livre.book ? `https://berti-law.net/${livre.book}` : null;

    const breadcrumbs = [
        {
            label: t('search.types.livres'),
            onClick: () => navigate(-1)
        },
        { label: livre.title || t('detail.book') }
    ];

    return (
        <div
            className="detail-page no-export"
            onContextMenu={(e) => e.preventDefault()}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onKeyDown={(e) => {
                if (e.ctrlKey && (e.key === 'p' || e.key === 'c' || e.key === 'x' || e.key === 's')) {
                    e.preventDefault();
                }
            }}
        >
            <Breadcrumbs items={breadcrumbs} />

            {/* Card 1: Attributes / Metadata */}
            <div className="result-card type-livre mb-4 animate-fade-in">
                <div className="result-card-body p-4">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div>
                            <span className="premium-badge badge-livre mb-2 d-inline-block">
                                <i className="bi bi-book me-2"></i>{t('search.types.livres')}
                            </span>
                            <h1 className="h3 fw-bold text-dark mb-2 result-title" style={{ fontSize: '1.75rem' }}>
                                {livre.title || t('detail.book')}
                            </h1>
                        </div>
                        <button
                            className="btn btn-outline-secondary rounded-pill px-4"
                            onClick={() => navigate(-1)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>{t('detail.back')}
                        </button>
                    </div>

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="row g-4">
                        {livre.auteur && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-success me-3">
                                        <i className="bi bi-person fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.author')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{livre.auteur}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {livre.annee && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-success me-3">
                                        <i className="bi bi-calendar3 fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.year')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{livre.annee}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {livre.editeur && (
                            <div className="col-md-6">
                                <div className="d-flex align-items-center">
                                    <div className="text-success me-3">
                                        <i className="bi bi-building fs-4"></i>
                                    </div>
                                    <div>
                                        <h6 className="text-secondary mb-0 text-uppercase small fw-bold" style={{ fontSize: '0.75rem' }}>{t('detail.publisher')}</h6>
                                        <p className="mb-0 fw-medium text-dark">{livre.editeur}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Card 2: Content */}
            {livre.contenu && (
                <div className="result-card type-livre mb-4 animate-fade-in">
                    <div className="result-card-body p-4">
                        <h4 className="fw-bold mb-4 text-dark border-start border-4 border-success ps-3">
                            {t('detail.content')}
                        </h4>
                        <div
                            className="content-text"
                            style={{ fontSize: '1.1rem', lineHeight: '1.8', fontFamily: 'Georgia, serif' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(livre.contenu || '') }}
                        />
                    </div>
                </div>
            )}

            {/* Card 3: Summary */}
            {livre.resume && (
                <div className="result-card type-livre animate-fade-in">
                    <div className="result-card-body p-4">
                        <h4 className="fw-bold mb-4 text-dark border-start border-4 border-success ps-3">
                            {t('detail.summary')}
                        </h4>
                        <div
                            className="content-text"
                            style={{ fontSize: '1.1rem', lineHeight: '1.8', fontFamily: 'Georgia, serif' }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(livre.resume || '') }}
                        />
                    </div>
                </div>
            )}

            {/* Card 4: PDF Viewer */}
            {(() => {
                const pdfUrl = i18n.language === 'ar' ? (livre.pdf_ar_url || livre.pdf_url) : livre.pdf_url;
                return pdfUrl ? (
                    <div id="pdf-viewer" className="result-card type-livre mb-4 animate-fade-in">
                        <div className="result-card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="fw-bold mb-0 text-dark border-start border-4 border-danger ps-3">
                                    <i className="bi bi-filetype-pdf me-2"></i>
                                    {i18n.language === 'ar' ? 'عرض ملف PDF' : 'Consulter le PDF'}
                                </h4>
                                <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                                   className="btn btn-outline-danger btn-sm rounded-pill">
                                    <i className="bi bi-box-arrow-up-right me-1"></i>
                                    {i18n.language === 'ar' ? 'فتح في نافذة جديدة' : 'Ouvrir dans un nouvel onglet'}
                                </a>
                            </div>
                            <iframe
                                src={pdfUrl}
                                style={{ width: '100%', height: '80vh', border: 'none', borderRadius: '8px' }}
                                title="PDF Viewer"
                            />
                        </div>
                    </div>
                ) : null;
            })()}
        </div>
    );
};

export default LivresDetail;

